/**
 * Notifications & SMS config (doc 01 A6, doc 13 T3; ADR 0013). Permission
 * `sms:manage`. Types mirror `SmsConfigResponse`.
 *
 * THE MODEL, WHICH IS THE ONLY THING A SCREEN HERE HAS TO GET RIGHT, CHANGED
 * WITH F436. Messaging is no longer a DEPLOYMENT-level integration with one
 * account for every organization. Each tenant connects THEIR OWN provider
 * account through the connector platform; the credential is tenant-scoped and
 * encrypted, and this surface carries the organization's messaging POLICY —
 * whether it is on, and which number it sends from.
 *
 * WHAT THAT MEANS FOR A SCREEN. `deploymentCredentialsConfigured` is gone. It
 * was a process-global boolean reported identically to every tenant, which was
 * honest under the old model and a lie under this one: one organization's
 * messaging can be broken while another sends fine. Its replacements are
 * per-tenant, and there are several rather than one because the single boolean
 * collapsed four situations with four different next actions — see
 * {@link SmsConfig.providerUnavailableReason}.
 *
 * A CREDENTIAL VALUE NEVER CROSSES THIS MODULE, and there is nowhere for one to
 * go: {@link UpdateSmsConfigInput} has two fields, neither a secret nor a
 * pointer to one. Entering a credential is an INTEGRATIONS call, not this one.
 *
 * It took three passes to get there. This module first declared
 * `twilioAccountSid` / `twilioAuthToken` — the secret VALUES — which invited a
 * screen to put a live auth token into a request body where a client-side log or
 * a proxy access log could keep it. Those became `*Ref` fields carrying Secret
 * Manager resource paths, which was safe but described a per-organization
 * credential model the product did not have; migration 1757470000000 dropped
 * them. F436 gives the product that model for real, in the one place that can
 * hold it.
 *
 * {@link updateConfig} STILL BUILDS THE REQUEST BODY ITSELF rather than
 * forwarding the caller's object, and that is not vestigial. TypeScript does not
 * excess-property-check a conditional spread (`...(sid ? { x: sid } : {})`),
 * which is exactly the shape a "only send it if the admin typed one" form
 * produces — the type alone would not stop a stray value reaching `fetch`. The
 * projection does: an unrecognised key is dropped in the client, before the
 * request exists. The API answers 400 for one too, because
 * `updateSmsConfigSchema` is `.strict()`; the projection is what keeps a value
 * from being transported at all to earn that 400.
 */
import { get, post, put } from './transport.js';
/**
 * `GET /api/admin/sms/config` → `{ config }`. Never contains secrets.
 *
 * Null when the organization has never saved a configuration — the service
 * answers `null` rather than inventing a row
 * (`apps/api/src/domains/sms/service.ts:166-171`), and a screen must render
 * "not configured" rather than reading fields off nothing.
 */
export async function config(options) {
    const body = await get('/api/admin/sms/config', options);
    return body.config ?? null;
}
/**
 * `PUT /api/admin/sms/config` → `{ config }`.
 *
 * Only the two keys of {@link UpdateSmsConfigInput} are put on the wire; see
 * this module's header for why the projection exists rather than trusting the
 * declared type. A key the caller left out stays out, so the API's
 * absent-means-unchanged rule survives the copy; a key explicitly set to `null`
 * is sent as `null` and clears the stored value.
 */
export async function updateConfig(input, options) {
    // Key presence, not value truthiness — the same test the API's own
    // `updateConfig` runs (service.ts:188). `undefined` is treated as absent
    // because that is what it becomes once JSON.stringify has run, so
    // `{ enabled: undefined }` cannot mean something here that it stops meaning
    // on the wire.
    const provided = (key) => Object.prototype.hasOwnProperty.call(input, key) && input[key] !== undefined;
    const payload = {};
    if (provided('enabled'))
        payload.enabled = input.enabled;
    if (provided('fromPhoneNumber'))
        payload.fromPhoneNumber = input.fromPhoneNumber;
    const body = await put('/api/admin/sms/config', payload, options);
    return body.config;
}
/**
 * `GET /api/admin/sms/status` — THIS ORGANIZATION'S messaging health.
 *
 * Makes no outbound call: it reports what the tenant's own installation already
 * says. It replaces `connectionTest()`, which POSTed to
 * `/api/admin/sms/connection-test` and made a real request to Twilio to test the
 * DEPLOYMENT'S environment credentials — a question no administrator asked,
 * answered somewhere that looked like the answer to the one they did. Testing a
 * connection is now an integrations call against the tenant's own installation.
 */
export function status(options) {
    return get('/api/admin/sms/status', options);
}
/**
 * `POST /api/admin/sms/consent` — records this organization's consent state for
 * one recipient.
 *
 * The record is ORG-SCOPED: it survives a change of provider, because the
 * recipient agreed to hear from the agency rather than from whichever CPaaS is
 * in front of it. The response carries only the LAST FOUR DIGITS of the number.
 */
export function recordConsent(toPhoneNumber, state, options) {
    return post('/api/admin/sms/consent', { toPhoneNumber, state }, options);
}
/**
 * `POST /api/admin/sms/test` — sends a real test SMS to `toPhoneNumber`.
 *
 * Resolves ONLY when the message was accepted by the provider; anything else
 * rejects with an {@link ApiError} carrying the API's own sentence. See
 * {@link SendTestResult} for why there is no falsy result to check.
 */
export function sendTest(toPhoneNumber, options) {
    return post('/api/admin/sms/test', { toPhoneNumber }, options);
}
//# sourceMappingURL=sms.js.map